import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Send, X } from 'lucide-react';
import { DateTimePicker } from '@/components/common/DateTimePicker';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Task, TaskStatus } from '../types';
import { useNewTaskStatusMutations } from '../hooks';

interface ReviewTaskDialogProps {
  task: Task;
  mode: 'review' | 'reject';
  /** When set, the button is disabled and this string is shown as a tooltip */
  disabledReason?: string;
  onSuccess?: () => void;
}

export function ReviewTaskDialog({ task, mode, disabledReason, onSuccess }: ReviewTaskDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [comment, setComment] = React.useState('');
  const [result, setResult] = React.useState('');
  const [dueAt, setDueAt] = React.useState<Date | undefined>(undefined);
  const { t } = useTranslation();
  const { changeStatusMutation } = useNewTaskStatusMutations();

  const todayStart = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const isReview = mode === 'review';
  const getButtonLabel = () => {
    if (!isReview) return t('reject');
    return t('sendToReview');
  };
  const buttonLabel = getButtonLabel();

  // Determine next status based on current status and mode
  const getNextStatus = (): TaskStatus => {
    if (isReview) {
      if (task.newStatus === 'NEW' || task.newStatus === 'LEAD_REJECT') return 'LEAD_PENDING';
      if (task.newStatus === 'PARTNER_PENDING' || task.newStatus === 'BILLING_REJECT') return 'BILLING_PENDING';
      return 'PARTNER_PENDING';
    } else {
      // Reject mode: map current pending status to its reject sibling.
      // BILLING_PENDING → BILLING_REJECT is MD pushing back to the partner.
      if (task.newStatus === 'BILLING_PENDING') return 'BILLING_REJECT';
      if (task.newStatus === 'PARTNER_PENDING') return 'PARTNER_REJECT';
      return 'LEAD_REJECT';
    }
  };

  // Seed the result field with the task's existing result whenever the dialog opens,
  // so the assignee can review/edit what was entered before.
  React.useEffect(() => {
    if (open && isReview) setResult(task.result ?? '');
  }, [open, isReview, task.result]);

  const handleConfirm = () => {
    const data: { newStatus: TaskStatus; comment?: string; result?: string; dueAt?: string } = { newStatus: getNextStatus() };
    if (isReview) {
      const trimmed = result.trim();
      if (trimmed) data.result = trimmed;
    } else {
      data.comment = comment;
      if (dueAt) data.dueAt = dueAt.toISOString();
    }
    changeStatusMutation.mutate(
      { id: task.id, data },
      {
        onSuccess: () => {
          setOpen(false);
          setComment('');
          setResult('');
          setDueAt(undefined);
          onSuccess?.();
        },
      },
    );
  };

  const triggerButton = (
    <Button
      size="sm"
      disabled={!!disabledReason}
      className={
        isReview
          ? 'bg-green-600 hover:bg-green-700 text-white'
          : 'bg-red-600 hover:bg-red-700 text-white'
      }
    >
      {isReview ? (
        <Send className="h-4 w-4" />
      ) : (
        <X className="h-4 w-4" />
      )}
      {buttonLabel}
    </Button>
  );

  // When disabled, show tooltip with the reason instead of dialog trigger
  if (disabledReason) {
    return (
      <TooltipProvider>
        <Tooltip>
          {/* span wrapper needed so tooltip works on a disabled button */}
          <TooltipTrigger asChild>
            <span tabIndex={0} className="inline-flex">
              {triggerButton}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {disabledReason}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {triggerButton}
      </DialogTrigger>

      <DialogContent className="!flex !flex-col sm:max-w-lg p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="text-xl font-semibold">
            {buttonLabel}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {buttonLabel}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('confirmMessageTask')}
          </p>
          {isReview && (
            <div className="space-y-2">
              <Label htmlFor="result">{t('taskResult')}</Label>
              <Textarea
                id="result"
                value={result}
                onChange={(e) => setResult(e.target.value)}
                placeholder={t('enterTaskResult')}
              />
            </div>
          )}
          {!isReview && (
            <>
              <div className="space-y-2">
                <Label htmlFor="comment">{t('comment')} <span className="text-red-500">*</span></Label>
                <Textarea
                  id="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('enterComment')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reject-due-at">{t('dueDate')} <span className="text-red-500">*</span></Label>
                <DateTimePicker
                  value={dueAt}
                  onChange={setDueAt}
                  mode="date"
                  minDate={todayStart}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t('cancel')}
            </Button>
          </DialogClose>
          <Button
            className={
              isReview
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }
            onClick={handleConfirm}
            disabled={changeStatusMutation.isPending || (!isReview && (!comment.trim() || !dueAt))}
          >
            {buttonLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
